import React, { useCallback, useEffect, useState } from 'react'
import { currentDate, daysOfWeek } from './constants'
import {
  ManagerBlock,
  CalendarModalContainer,
  MonthDaysContainer,
  DaysStringContainer,
  DaysNumbersContainer,
  DaysNumber,
  Footer,
  FooterButton,
  Header,
  PeriodManager,
  SelectedDatesContainer,
  SelectedDatesLabel,
  ClearSelectedDatesButton,
  SelectedDate,
  CloseButton,
  LastDates,
  Switch
} from './styles'
import * as utils from './utils'
import { IconComponent } from '~/assets/icons/component'
import { Divider, Inline, Stack, theme } from '~/styles'
import { transformDate } from '~/utils'

export interface DatePickerMultipleProps {
  date?: Date[]
  enabledDays?: number[]
  maxOfSelectedDates?: number
  onConfirm: (PeriodSelected: Date[]) => void
  onAction?: (action: 'close' | 'confirm') => void
}

/**
 * This component will render a date picker with the days available
 * @param {DatePickerMultipleProps['date']=} date - array of dates to be selected
 * @param {DatePickerMultipleProps['enabledDays']=} enabledDays - array of dates to be available to be selected
 * @param {DatePickerMultipleProps['maxOfSelectedDates']=} maxOfSelectedDates - max of dates that can be selected
 * @param {DatePickerMultipleProps['onConfirm']} onConfirm - callback function to be called when the user confirm the selected dates
 * @param {DatePickerMultipleProps['onAction']} onAction - callback function to be called when the user interact with some button
 * @returns {JSX.Element} return the rendered calendar
 * @example
 * const [date, setDate] = useState<Date[]>([])
 * <Calendar onConfirm={setDate} date={date} enabledDays={date} maxOfSelectedDates={6} />
 */

export const DatePickerMultiple = ({
  date,
  enabledDays,
  maxOfSelectedDates,
  onConfirm,
  onAction
}: DatePickerMultipleProps): JSX.Element => {
  const initialDate = date && date.length > 0 ? date : [currentDate]
  const [mainVisiblePeriod, setMainVisiblePeriod] = useState<Date>(initialDate[initialDate.length - 1])
  const [selectedDates, setSelectedDates] = useState<Date[]>(date ?? [])
  const [isLastDatesSelected, setIsLastDatesSelected] = useState<boolean>(false)

  useEffect(() => {
    const hasExternalDateReset = date && date.length === 0

    if (hasExternalDateReset) {
      setSelectedDates([])
    }
  }, [date])

  const submitCalendar = () => {
    onConfirm(selectedDates)
    if (onAction) onAction('confirm')
  }

  const resetAllSelectedDates = (e: React.MouseEvent) => {
    e.stopPropagation()
    setSelectedDates([])
  }

  const removeSelectedDate = useCallback(
    (dateToRemove: Date) => {
      const newSelectedDates = selectedDates.filter(date => date.getTime() !== dateToRemove.getTime())
      setSelectedDates(newSelectedDates)
    },
    [selectedDates]
  )

  const toggleLastDatesSelection = useCallback(() => {
    const toggleNewState = !isLastDatesSelected

    if (!toggleNewState) {
      setSelectedDates([])
    }
    if (toggleNewState && maxOfSelectedDates && enabledDays) {
      const transformedEnabledDays = enabledDays.slice(0, maxOfSelectedDates)
      setSelectedDates(transformedEnabledDays?.map(day => new Date(day)) ?? [])
    }

    setIsLastDatesSelected(toggleNewState)
  }, [enabledDays, maxOfSelectedDates, isLastDatesSelected])

  const backwardPeriodInMonthsToUpdateCalendar = useCallback(
    (monthQuantity = 1) => {
      const previousDate = utils.getPreviousDateWithMonthPeriod(mainVisiblePeriod, monthQuantity)
      setMainVisiblePeriod(previousDate)
    },
    [mainVisiblePeriod]
  )

  const forwardPeriodInMothsToUpdateCalendar = useCallback(
    (monthQuantity = 1) => {
      const futureDate = utils.getNextDateWithMonthPeriod(mainVisiblePeriod, monthQuantity)
      setMainVisiblePeriod(futureDate)
    },
    [mainVisiblePeriod]
  )

  const handleSelectDay = (day: Date) => {
    if (utils.verifyIfMonthIsDifferent(day, mainVisiblePeriod)) {
      day > mainVisiblePeriod ? forwardPeriodInMothsToUpdateCalendar() : backwardPeriodInMonthsToUpdateCalendar()
      return
    }

    const newSelectedDates = utils.returnNewSelectedDates(day, selectedDates)
    if (maxOfSelectedDates && newSelectedDates.length > maxOfSelectedDates) {
      return
    }

    setSelectedDates(newSelectedDates)
  }

  const onCLose = () => {
    if (onAction) onAction('close')
  }

  return (
    <CalendarModalContainer>
      <Inline width="100%" height="100%" spacing="0" alignItems="flex-start">
        <Stack spacing="0">
          <Header>
            <LastDates>Últimas {maxOfSelectedDates} datas</LastDates>

            <Switch>
              <input type="checkbox" checked={isLastDatesSelected} onChange={toggleLastDatesSelection} />
              <span />
            </Switch>
          </Header>

          <Divider borderColor={theme.colors.colorDCE3ED} />

          <PeriodManager>
            <ManagerBlock type="year">
              <button onClick={() => backwardPeriodInMonthsToUpdateCalendar(12)} data-testid="prev-year-arrow">
                <IconComponent name="chevronLeft" />
              </button>
              <span>{utils.getCurrentYear(mainVisiblePeriod)}</span>
              <button onClick={() => forwardPeriodInMothsToUpdateCalendar(12)} data-testid="next-year-arrow">
                <IconComponent name="chevronRight" />
              </button>
            </ManagerBlock>
            <ManagerBlock type="month">
              <button onClick={() => backwardPeriodInMonthsToUpdateCalendar()} data-testid="prev-month-arrow">
                <IconComponent name="chevronLeft" />
              </button>
              <span>{utils.getMonthName(mainVisiblePeriod)}</span>
              <button onClick={() => forwardPeriodInMothsToUpdateCalendar()} data-testid="next-month-arrow">
                <IconComponent name="chevronRight" />
              </button>
            </ManagerBlock>
          </PeriodManager>

          <MonthDaysContainer>
            <DaysStringContainer>
              {daysOfWeek.map(day => {
                return (
                  <span key={day} title={day}>
                    {day.slice(0, 1)}
                  </span>
                )
              })}
            </DaysStringContainer>
            <DaysNumbersContainer data-testid="days-container">
              {utils.createDaysAvailableInPeriod(mainVisiblePeriod).map(day => {
                const isSelected = utils.verifyIfDayExists(day, selectedDates)
                const isDayOfOtherMonth = utils.verifyIfMonthIsDifferent(day, mainVisiblePeriod)

                return (
                  <DaysNumber
                    key={`${day.getTime()}`}
                    onClick={() => handleSelectDay(day)}
                    isSelectedDay={isSelected}
                    disabled={
                      (enabledDays && !enabledDays?.includes(day.getTime())) || isDayOfOtherMonth || isLastDatesSelected
                    }
                  >
                    <span>{day.getDate()}</span>
                  </DaysNumber>
                )
              })}
            </DaysNumbersContainer>
          </MonthDaysContainer>
        </Stack>

        {selectedDates.length > 0 && (
          <>
            <Divider orientation="vertical" borderColor={theme.colors.colorDCE3ED} />

            <SelectedDatesContainer>
              <Stack>
                <SelectedDatesLabel>Datas</SelectedDatesLabel>

                {selectedDates.map(date => (
                  <Inline key={date.getTime()}>
                    <SelectedDate data-testid="selected-date">
                      {transformDate({ date, dateFormat: 'dd/MM/yy' })}
                    </SelectedDate>
                    <CloseButton
                      onClick={e => {
                        e.stopPropagation()
                        removeSelectedDate(date)
                      }}
                    >
                      <IconComponent name="close" />
                    </CloseButton>
                  </Inline>
                ))}
              </Stack>

              <ClearSelectedDatesButton onClick={resetAllSelectedDates}>Limpar todos</ClearSelectedDatesButton>
            </SelectedDatesContainer>
          </>
        )}
      </Inline>

      <Divider borderColor={theme.colors.colorDCE3ED} />

      <Footer>
        <FooterButton onClick={onCLose}>fechar</FooterButton>
        <FooterButton isActive onClick={submitCalendar}>
          confirmar
        </FooterButton>
      </Footer>
    </CalendarModalContainer>
  )
}
